import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import localStorageService from './services/localStorageService';
import wordGenerator from './services/wordGenerator';
import './Schedule.css';

const Schedule = () => {
  const navigate = useNavigate();
  const { isGuest } = useAuth();
  const [loading, setLoading] = useState(false);

  // ===== 核心数据结构（本地管理） =====
  const [scheduleList, setScheduleList] = useState([]); // [{date_str, start_time, items:[{duration, content}]}]
  const [currentDate, setCurrentDate] = useState(null);  // 当前选中日期
  const [scheduleTitle, setScheduleTitle] = useState('');
  const [historyList, setHistoryList] = useState([]);
  const [loadedHistoryId, setLoadedHistoryId] = useState(null);

  // 日期/事项添加表单
  const [newDate, setNewDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('08:30');
  const [duration, setDuration] = useState('');
  const [content, setContent] = useState('');

  // 预览
  const [generatedText, setGeneratedText] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // 编辑
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDuration, setEditDuration] = useState('');
  const [editContent, setEditContent] = useState('');

  // ===== 初始化 =====
  useEffect(() => {
    loadFromLocalStorage();
    loadHistory();
  }, []);

  // 从 localStorage 恢复数据
  const loadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('schedule_current');
      if (saved) {
        const data = JSON.parse(saved);
        setScheduleList(data.scheduleList || []);
        setCurrentDate(data.currentDate || null);
        setScheduleTitle(data.title || '');
      }
    } catch (e) {
      console.error('加载本地数据失败:', e);
    }
  };

  // 保存到 localStorage
  const saveToLocalStorage = (list, date, title) => {
    try {
      localStorage.setItem('schedule_current', JSON.stringify({
        scheduleList: list,
        currentDate: date,
        title: title
      }));
    } catch (e) {
      console.error('保存本地数据失败:', e);
    }
  };

  // 加载历史记录（IndexedDB）
  const loadHistory = async () => {
    try {
      const schedules = await localStorageService.getAllSchedules();
      setHistoryList(schedules || []);
    } catch (e) {
      console.error('加载历史记录失败:', e);
      setHistoryList([]);
    }
  };

  // ===== 辅助方法 =====
  const getDates = () => scheduleList.map(item => item.date_str).sort();

  const getCurrentSchedule = () => {
    if (!currentDate) return null;
    return scheduleList.find(item => item.date_str === currentDate) || null;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    return `${dateStr} (${weekdays[date.getDay()]})`;
  };

  const calculateTimeRange = (startTime, duration, index, items) => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    let currentHour = startHour;
    let currentMinute = startMinute;

    for (let i = 0; i < index; i++) {
      const totalMinutes = currentHour * 60 + currentMinute + items[i].duration;
      currentHour = Math.floor(totalMinutes / 60);
      currentMinute = totalMinutes % 60;
    }

    const totalMinutes = currentHour * 60 + currentMinute + duration;
    const endHour = Math.floor(totalMinutes / 60);
    const endMinute = totalMinutes % 60;

    return `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}--${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
  };

  const checkLogin = () => {
    if (isGuest) {
      navigate('/login');
      return false;
    }
    return true;
  };

  // ===== 日期操作 =====
  const handleAddDate = () => {
    if (!newDate) {
      alert('请选择日期');
      return;
    }
    // 检查重复
    if (scheduleList.some(item => item.date_str === newDate)) {
      alert('该日期已存在');
      return;
    }

    const newList = [...scheduleList, {
      date_str: newDate,
      start_time: newStartTime,
      items: []
    }].sort((a, b) => a.date_str.localeCompare(b.date_str));

    setScheduleList(newList);
    setCurrentDate(newDate);
    saveToLocalStorage(newList, newDate, scheduleTitle);
    setNewDate('');
    setNewStartTime('08:30');
  };

  const handleDeleteDate = (dateStr) => {
    if (!window.confirm('确定要删除这个日期及其所有行程吗?')) return;

    const newList = scheduleList.filter(item => item.date_str !== dateStr);
    let newCurrentDate = currentDate;
    if (currentDate === dateStr) {
      newCurrentDate = newList.length > 0 ? newList[0].date_str : null;
    }
    setScheduleList(newList);
    setCurrentDate(newCurrentDate);
    saveToLocalStorage(newList, newCurrentDate, scheduleTitle);
  };

  const handleSelectDate = (dateStr) => {
    setCurrentDate(dateStr);
    saveToLocalStorage(scheduleList, dateStr, scheduleTitle);
  };

  // ===== 行程事项操作 =====
  const handleAddItem = () => {
    if (!duration || !content) {
      alert('请填写时长和内容');
      return;
    }
    if (!currentDate) {
      alert('请先选择日期');
      return;
    }

    const newList = scheduleList.map(item => {
      if (item.date_str === currentDate) {
        return { ...item, items: [...item.items, { duration: parseInt(duration), content }] };
      }
      return item;
    });

    setScheduleList(newList);
    saveToLocalStorage(newList, currentDate, scheduleTitle);
    setDuration('');
    setContent('');
  };

  const handleDeleteItem = (index) => {
    if (!window.confirm('确定要删除这个行程事项吗?')) return;

    const newList = scheduleList.map(item => {
      if (item.date_str === currentDate) {
        const newItems = item.items.filter((_, i) => i !== index);
        return { ...item, items: newItems };
      }
      return item;
    });

    setScheduleList(newList);
    saveToLocalStorage(newList, currentDate, scheduleTitle);
  };

  const handleMoveUp = (index) => {
    const newList = scheduleList.map(item => {
      if (item.date_str === currentDate && index > 0) {
        const newItems = [...item.items];
        [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
        return { ...item, items: newItems };
      }
      return item;
    });

    setScheduleList(newList);
    saveToLocalStorage(newList, currentDate, scheduleTitle);
  };

  const handleMoveDown = (index) => {
    const newList = scheduleList.map(item => {
      if (item.date_str === currentDate && index < item.items.length - 1) {
        const newItems = [...item.items];
        [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
        return { ...item, items: newItems };
      }
      return item;
    });

    setScheduleList(newList);
    saveToLocalStorage(newList, currentDate, scheduleTitle);
  };

  const handleInsertItem = (index) => {
    const insertDuration = prompt('请输入时长（分钟）:');
    if (!insertDuration) return;
    const insertContent = prompt('请输入行程内容:');
    if (!insertContent) return;

    const newList = scheduleList.map(item => {
      if (item.date_str === currentDate) {
        const newItems = [...item.items];
        newItems.splice(index, 0, { duration: parseInt(insertDuration), content: insertContent });
        return { ...item, items: newItems };
      }
      return item;
    });

    setScheduleList(newList);
    saveToLocalStorage(newList, currentDate, scheduleTitle);
  };

  const handleEditItem = (index, item) => {
    setEditingIndex(index);
    setEditDuration(item.duration.toString());
    setEditContent(item.content);
  };

  const handleSaveEdit = (index) => {
    if (!editDuration || !editContent) {
      alert('请填写完整信息');
      return;
    }

    const newList = scheduleList.map(item => {
      if (item.date_str === currentDate) {
        const newItems = [...item.items];
        newItems[index] = { duration: parseInt(editDuration), content: editContent };
        return { ...item, items: newItems };
      }
      return item;
    });

    setScheduleList(newList);
    saveToLocalStorage(newList, currentDate, scheduleTitle);
    setEditingIndex(null);
    setEditDuration('');
    setEditContent('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditDuration('');
    setEditContent('');
  };

  const handleEditStartTime = () => {
    if (!currentDate) {
      alert('请先选择或添加日期');
      return;
    }
    const schedule = getCurrentSchedule();
    if (!schedule) return;

    const newTime = prompt('请输入新的开始时间（格式：HH:MM，例如 08:30）:', schedule.start_time);
    if (!newTime) return;

    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(newTime)) {
      alert('时间格式不正确，请使用 HH:MM 格式（例如 08:30）');
      return;
    }

    const newList = scheduleList.map(item => {
      if (item.date_str === currentDate) {
        return { ...item, start_time: newTime };
      }
      return item;
    });

    setScheduleList(newList);
    saveToLocalStorage(newList, currentDate, scheduleTitle);
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setScheduleTitle(newTitle);
    saveToLocalStorage(scheduleList, currentDate, newTitle);
  };

  // ===== 生成文本（本地） =====
  const generateScheduleText = () => {
    const lines = [];
    const sortedList = [...scheduleList].sort((a, b) => a.date_str.localeCompare(b.date_str));

    for (const schedule of sortedList) {
      // 解析日期
      const dateObj = new Date(schedule.date_str);
      const month = dateObj.getMonth() + 1;
      const day = dateObj.getDate();
      const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
      const weekday = weekdays[dateObj.getDay()];

      // 日期行
      lines.push(`${month}月${day}日（${weekday}）`);

      // 解析开始时间
      const [startHour, startMinute] = schedule.start_time.split(':').map(Number);
      let currentHour = startHour;
      let currentMinute = startMinute;

      // 生成每个行程事项
      for (const item of schedule.items) {
        const totalMinutes = currentHour * 60 + currentMinute + item.duration;
        const endHour = Math.floor(totalMinutes / 60);
        const endMinute = totalMinutes % 60;

        const startStr = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
        const endStr = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;

        lines.push(`${startStr}--${endStr} ${item.content}`);

        currentHour = endHour;
        currentMinute = endMinute;
      }

      lines.push(''); // 日期间空行
    }

    // 移除最后空行
    if (lines.length > 0 && lines[lines.length - 1] === '') {
      lines.pop();
    }

    return lines.join('\n');
  };

  const handleGenerateText = () => {
    if (scheduleList.length === 0) {
      alert('请先添加日期和行程事项');
      return;
    }

    const text = generateScheduleText();
    setGeneratedText(text);
    setShowPreview(true);
  };

  // ===== 生成Word（本地，使用docx.js，公文格式） =====
  const handleGenerateWord = async () => {
    if (scheduleList.length === 0) {
      alert('请先添加日期和行程事项');
      return;
    }

    setLoading(true);
    try {
      const text = generateScheduleText();
      const title = scheduleTitle || '行程安排';
      await wordGenerator.generateSchedule({
        title,
        scheduleText: text
      });

      // 自动保存到历史记录
      try {
        const scheduleData = {
          title,
          scheduleList: scheduleList,
          currentDate: currentDate
        };

        if (loadedHistoryId) {
          await localStorageService.saveSchedule({
            id: loadedHistoryId,
            ...scheduleData
          });
        } else {
          const savedData = await localStorageService.saveSchedule(scheduleData);
          if (savedData && savedData.id) {
            setLoadedHistoryId(savedData.id);
          }
        }
        await loadHistory();
      } catch (saveError) {
        console.error('自动保存历史记录失败:', saveError);
      }
    } catch (error) {
      console.error('生成Word文档失败:', error);
      alert('生成Word文档失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== 填充示例 =====
  const handleFillExample = () => {
    if (!window.confirm('填充示例将清空当前行程并填充示例数据，是否继续？')) return;

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];

    const exampleTitle = '关于赴广州开展考察调研的行程排版';
    const exampleList = [{
      date_str: dateStr,
      start_time: '08:30',
      items: [
        { duration: 30, content: '在单位集合，准备出发' },
        { duration: 60, content: '乘车前往广州（途中讨论调研方案）' },
        { duration: 90, content: '抵达广州，入住酒店' },
        { duration: 60, content: '午餐' },
        { duration: 120, content: '前往XX公司考察调研，听取工作汇报' },
        { duration: 60, content: '实地参观生产车间和研发中心' },
        { duration: 60, content: '晚餐' },
        { duration: 30, content: '返回酒店休息' }
      ]
    }];

    setScheduleList(exampleList);
    setCurrentDate(dateStr);
    setScheduleTitle(exampleTitle);
    saveToLocalStorage(exampleList, dateStr, exampleTitle);
  };

  // ===== 清空 =====
  const handleClearAll = () => {
    if (!window.confirm('确定要清空所有行程吗?此操作不可恢复!')) return;

    setScheduleList([]);
    setCurrentDate(null);
    setScheduleTitle('');
    setLoadedHistoryId(null);
    saveToLocalStorage([], null, '');
  };

  // ===== 新建行程 =====
  const handleNewSchedule = () => {
    const hasData = scheduleList.length > 0 || scheduleTitle.trim();
    if (hasData) {
      if (!window.confirm('新建行程将清空当前所有数据，是否继续？')) return;
    }

    setScheduleList([]);
    setCurrentDate(null);
    setScheduleTitle('');
    setLoadedHistoryId(null);
    saveToLocalStorage([], null, '');
  };

  // ===== 历史记录（本地IndexedDB） =====
  const handleQuickSave = async () => {
    if (scheduleList.length === 0) {
      alert('请先添加日期');
      return;
    }

    let title = scheduleTitle;
    if (!title) {
      title = `行程排版_${new Date().toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/[\/\s:]/g, '-')}`;
    }

    try {
      const scheduleData = {
        title,
        scheduleList: scheduleList,
        currentDate: currentDate
      };

      if (loadedHistoryId) {
        // 更新
        await localStorageService.saveSchedule({
          id: loadedHistoryId,
          ...scheduleData
        });
        alert('行程排版更新成功');
      } else {
        // 新建
        const savedData = await localStorageService.saveSchedule(scheduleData);
        if (savedData && savedData.id) {
          setLoadedHistoryId(savedData.id);
        }
        alert('行程排版保存成功');
      }

      await loadHistory();
    } catch (error) {
      console.error('保存失败:', error);
      alert('保存失败');
    }
  };

  const handleLoadHistory = async (historyId) => {
    if (!window.confirm('加载历史记录将覆盖当前行程，确定继续？')) return;

    try {
      const data = await localStorageService.getSchedule(historyId);
      if (data) {
        setScheduleList(data.scheduleList || []);
        setCurrentDate(data.currentDate || (data.scheduleList && data.scheduleList.length > 0 ? data.scheduleList[0].date_str : null));
        setScheduleTitle(data.title || '');
        setLoadedHistoryId(historyId);
        saveToLocalStorage(data.scheduleList || [], data.currentDate || null, data.title || '');
        alert('历史记录加载成功');
      }
    } catch (error) {
      console.error('加载失败:', error);
      alert('加载失败');
    }
  };

  const handleDeleteHistory = async (historyId) => {
    if (!window.confirm('确定要删除这条历史记录吗？')) return;

    try {
      await localStorageService.deleteSchedule(historyId);
      if (loadedHistoryId === historyId) {
        setLoadedHistoryId(null);
      }
      await loadHistory();
    } catch (error) {
      console.error('删除失败:', error);
      alert('删除失败');
    }
  };

  // ===== 获取当前schedule对象（兼容旧UI） =====
  const schedule = getCurrentSchedule();
  const dates = getDates();

  return (
    <div className="schedule-container">
      <div className="schedule-content">
        {/* 左侧面板：历史记录和日期列表上下排列 */}
        <div className="left-panel">
          {/* 历史记录栏 */}
          <div className="history-panel">
            <div className="panel-header">
              <h3>历史记录</h3>
            </div>
            <div className="history-list">
              {historyList.length === 0 ? (
                <div className="empty-history">暂无历史记录</div>
              ) : (
                historyList.map((item) => (
                  <div key={item.id} className="history-item">
                    <div className="history-title-row">
                      <div className="history-title">{item.title}</div>
                      <div className="history-actions">
                        <button
                          className="load-btn"
                          onClick={() => handleLoadHistory(item.id)}
                          disabled={loading}
                        >
                          加载
                        </button>
                        <button
                          className="delete-history-btn"
                          onClick={() => handleDeleteHistory(item.id)}
                          disabled={loading}
                        >
                          删除
                        </button>
                      </div>
                    </div>
                    <div className="history-time">{item.updatedAt || item.createdAt}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* 日期列表 */}
          <div className="dates-panel">
            <div className="panel-header">
              <h3>日期列表</h3>
              <span className="date-count">{dates.length} 天</span>
            </div>

            <div className="add-date-form">
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
              />
              <input
                type="time"
                value={newStartTime}
                onChange={(e) => setNewStartTime(e.target.value)}
              />
              <button
                className="add-btn"
                onClick={handleAddDate}
                disabled={loading}
              >
                添加日期
              </button>
            </div>

            <div className="dates-list">
              {dates.map((date, index) => (
                <div
                  key={`${date}-${index}`}
                  className={`date-item ${currentDate === date ? 'active' : ''}`}
                  onClick={() => handleSelectDate(date)}
                >
                  <span className="date-text">{formatDate(date)}</span>
                  <button
                    className="delete-date-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDate(date);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              {dates.length === 0 && (
                <div className="empty-state">暂无日期,请添加</div>
              )}
            </div>

            <div className="panel-actions">
              <button
                className="action-btn danger"
                onClick={handleClearAll}
                disabled={loading || dates.length === 0}
              >
                清空所有
              </button>
            </div>
          </div>
        </div>

        {/* 右侧行程详情 */}
        <div className="schedule-panel">
          <div className="panel-header">
            <h3>行程详情</h3>
            <div className="header-actions">
              <button
                className="fill-example-btn"
                onClick={handleFillExample}
                disabled={loading}
                title="填充示例行程"
              >
                📝 填充示例
              </button>
              {schedule && (
                <span
                  className="start-time editable"
                  onClick={handleEditStartTime}
                  title="点击修改开始时间"
                >
                  开始时间: {schedule.start_time}
                </span>
              )}
            </div>
          </div>

          {/* 行程主题 */}
          <div className="schedule-title-section">
            <input
              type="text"
              placeholder="请输入行程主题..."
              value={scheduleTitle}
              onChange={handleTitleChange}
              className="schedule-title-input"
            />
            <button
              className="new-schedule-btn"
              onClick={handleNewSchedule}
              disabled={loading}
              title="新建行程"
            >
              新建行程
            </button>
          </div>

          {schedule ? (
            <>
              <div className="add-item-form">
                <input
                  type="number"
                  placeholder="时长(分钟)"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  min="1"
                />
                <input
                  type="text"
                  placeholder="行程内容"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
                />
                <button
                  className="add-btn"
                  onClick={handleAddItem}
                  disabled={loading}
                >
                  添加事项
                </button>
              </div>

              <div className="items-list">
                {/* 桌面端表格布局 */}
                <table className="items-table">
                  <thead>
                    <tr>
                      <th width="50">序号</th>
                      <th width="130">时间段</th>
                      <th width="80">时长(分钟)</th>
                      <th>内容</th>
                      <th width="260">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.items.map((item, index) => (
                      <tr key={index}>
                        <td className="center">{index + 1}</td>
                        <td className="center">
                          {calculateTimeRange(schedule.start_time, item.duration, index, schedule.items)}
                        </td>
                        {editingIndex === index ? (
                          <>
                            <td className="center">
                              <input
                                type="number"
                                className="edit-input"
                                value={editDuration}
                                onChange={(e) => setEditDuration(e.target.value)}
                                placeholder="时长"
                                autoFocus
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="edit-input content-input"
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                placeholder="内容"
                              />
                            </td>
                            <td className="center">
                              <div className="action-buttons">
                                <button
                                  className="save-btn"
                                  onClick={() => handleSaveEdit(index)}
                                  disabled={loading}
                                >
                                  保存
                                </button>
                                <button
                                  className="cancel-btn"
                                  onClick={handleCancelEdit}
                                  disabled={loading}
                                >
                                  取消
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td
                              className="center editable-cell"
                              onClick={() => handleEditItem(index, item)}
                              title="点击编辑"
                            >
                              {item.duration}
                            </td>
                            <td
                              className="editable-cell"
                              onClick={() => handleEditItem(index, item)}
                              title="点击编辑"
                            >
                              {item.content}
                            </td>
                            <td className="center">
                              <div className="action-buttons">
                                <button
                                  className="move-btn"
                                  onClick={() => handleMoveUp(index)}
                                  disabled={loading || index === 0}
                                  title="上移"
                                >
                                  ↑
                                </button>
                                <button
                                  className="move-btn"
                                  onClick={() => handleMoveDown(index)}
                                  disabled={loading || index === schedule.items.length - 1}
                                  title="下移"
                                >
                                  ↓
                                </button>
                                <button
                                  className="insert-btn"
                                  onClick={() => handleInsertItem(index)}
                                  disabled={loading}
                                  title="在此处插入"
                                >
                                  插入
                                </button>
                                <button
                                  className="delete-btn"
                                  onClick={() => handleDeleteItem(index)}
                                  disabled={loading}
                                >
                                  删除
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* 移动端卡片式布局 */}
                <div className="mobile-items-list">
                  {schedule.items.map((item, index) => (
                    <div
                      key={index}
                      className={`mobile-item-card ${editingIndex === index ? 'mobile-edit-card' : ''}`}
                    >
                      {editingIndex === index ? (
                        <>
                          <div className="mobile-item-header">
                            <span className="mobile-item-index">第 {index + 1} 项</span>
                            <span className="mobile-item-time">
                              {calculateTimeRange(schedule.start_time, item.duration, index, schedule.items)}
                            </span>
                          </div>
                          <input
                            type="number"
                            className="mobile-edit-input"
                            value={editDuration}
                            onChange={(e) => setEditDuration(e.target.value)}
                            placeholder="时长（分钟）"
                            autoFocus
                          />
                          <input
                            type="text"
                            className="mobile-edit-input"
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            placeholder="行程内容"
                          />
                          <div className="mobile-item-actions">
                            <button
                              className="save-btn"
                              onClick={() => handleSaveEdit(index)}
                              disabled={loading}
                            >
                              保存
                            </button>
                            <button
                              className="cancel-btn"
                              onClick={handleCancelEdit}
                              disabled={loading}
                            >
                              取消
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mobile-item-header">
                            <span className="mobile-item-index">第 {index + 1} 项</span>
                            <span className="mobile-item-time">
                              {calculateTimeRange(schedule.start_time, item.duration, index, schedule.items)}
                            </span>
                          </div>
                          <div className="mobile-item-content">{item.content}</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span className="mobile-item-duration">{item.duration} 分钟</span>
                          </div>
                          <div className="mobile-item-actions">
                            <button
                              className="move-btn"
                              onClick={() => handleMoveUp(index)}
                              disabled={loading || index === 0}
                              title="上移"
                            >
                              ↑ 上移
                            </button>
                            <button
                              className="move-btn"
                              onClick={() => handleMoveDown(index)}
                              disabled={loading || index === schedule.items.length - 1}
                              title="下移"
                            >
                              ↓ 下移
                            </button>
                            <button
                              className="insert-btn"
                              onClick={() => handleInsertItem(index)}
                              disabled={loading}
                            >
                              插入
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteItem(index)}
                              disabled={loading}
                            >
                              删除
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {schedule.items.length === 0 && (
                  <div className="empty-state">暂无行程事项,请添加</div>
                )}
              </div>

              <div className="panel-actions">
                <button
                  className="action-btn save-action"
                  onClick={handleQuickSave}
                  disabled={loading || schedule.items.length === 0}
                >
                  保存
                </button>
                <button
                  className="action-btn primary"
                  onClick={handleGenerateText}
                  disabled={loading || schedule.items.length === 0}
                >
                  生成文本
                </button>
                <button
                  className="action-btn success"
                  onClick={handleGenerateWord}
                  disabled={loading || schedule.items.length === 0}
                >
                  生成Word文档
                </button>
              </div>
            </>
          ) : (
            <div className="empty-state large">
              请先选择或添加日期
            </div>
          )}
        </div>
      </div>

      {/* 预览弹窗 */}
      {showPreview && (
        <div className="preview-modal">
          <div className="preview-content">
            <div className="preview-header">
              <h3>行程预览</h3>
              <button
                className="close-btn"
                onClick={() => setShowPreview(false)}
              >
                ×
              </button>
            </div>
            <div className="preview-body">
              <pre>{generatedText}</pre>
            </div>
            <div className="preview-footer">
              <button
                className="copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(generatedText);
                  alert('已复制到剪贴板');
                }}
              >
                复制文本
              </button>
              <button
                className="close-preview-btn"
                onClick={() => setShowPreview(false)}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;
